import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getTenants } from '../services/tenantService';
import { register } from '../services/authService';
import { getMyClaim, submitClaim } from '../services/membershipService';
import { useAuth } from '../context/AuthContext';
import RSMinSignInButton from '../components/RSMinSignInButton';

/**
 * TenantJoinPage — per-tenant branded join/signup page.
 *
 * Route: /t/:tenantSlug/join  (public — no auth required)
 *
 * Behaviour:
 *  - Not logged in → creates an account and pending application together.
 *  - Logged in → shows a one-click membership claim form if they haven't claimed yet.
 *
 * Branding: tenant name, logo, primaryColor from Tenant.branding are applied inline.
 */
const TenantJoinPage = () => {
  const { tenantSlug } = useParams();
  const { user, memberships = [], loadSession } = useAuth();
  const userId = user?._id;

  const [tenant, setTenant]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [revision, setRevision] = useState(0);
  const [claim, setClaim] = useState(null);
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState('');

  // Registration form state (unauthenticated path)
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');

  // Claim form state (authenticated path)
  const [claimNote, setClaimNote] = useState('');

  const [submitting, setSub]  = useState(false);
  const [msg, setMsg]         = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let current = true;
    setLoading(true);
    setTenant(null);
    setNotFound(false);
    setLoadError('');
    setSuccess('');
    setMsg('');
    getTenants()
      .then((res) => {
        if (!current) return;
        const match = res.data.find((entry) => entry.slug === tenantSlug);
        if (match) setTenant(match);
        else setNotFound(true);
      })
      .catch((err) => {
        if (current) setLoadError(err.response?.data?.message || 'Unable to load this community. Please retry.');
      })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [tenantSlug, revision]);

  useEffect(() => {
    let current = true;
    setClaim(null);
    setClaimError('');
    if (!userId || !tenant) {
      setClaimLoading(false);
      return;
    }
    setClaimLoading(true);
    getMyClaim(tenant._id)
      .then((res) => { if (current) setClaim(res.data); })
      .catch((err) => {
        if (current && err.response?.status !== 404) {
          setClaimError(err.response?.data?.message || 'Unable to load your registration status. Please retry.');
        }
      })
      .finally(() => { if (current) setClaimLoading(false); });
    return () => { current = false; };
  }, [userId, tenant, revision]);

  // ── Unauthenticated: register account + auto-submit membership claim ───────
  const handleRegisterAndJoin = async (e) => {
    e.preventDefault();
    setMsg('');
    if (password.length < 8) { setMsg('Password must be at least 8 characters.'); return; }
    if (password !== confirm)  { setMsg('Passwords do not match.');               return; }

    setSub(true);
    try {
      await register({ firstName, lastName, email, password, tenantId: tenant._id });
      setSuccess(
        `Registration received. An administrator for ${tenant.branding?.displayName || tenant.name} must approve your application and assign access before you can use community tools.`
      );
    } catch (err) {
      setMsg(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setSub(false);
    }
  };

  // ── Authenticated: submit membership claim ────────────────────────────────
  const handleClaim = async (e) => {
    e.preventDefault();
    setMsg('');
    setSub(true);
    try {
      const res = await submitClaim({ tenantId: tenant._id, supportingInfo: { notes: claimNote } });
      setClaim(res.data);
      setSuccess(
        `Your membership request for ${tenant.branding?.displayName || tenant.name} has been submitted. The community administrator will review it shortly.`
      );
    } catch (err) {
      setMsg(err.response?.data?.message || 'Could not submit claim. Please try again.');
    } finally {
      setSub(false);
    }
  };

  // ── Render states ─────────────────────────────────────────────────────────
  if (loading) return <div className="page-loading">Loading…</div>;
  if (loadError) return <div className="page join-page" role="alert"><p>{loadError}</p>
    <button type="button" onClick={() => setRevision((value) => value + 1)}>Retry community</button></div>;

  if (notFound) {
    return (
      <div className="page join-page">
        <h2>Community Not Found</h2>
        <p>The community <code>{tenantSlug}</code> does not exist or is not accepting members at this time.</p>
        <Link to="/">Return to Home</Link>
      </div>
    );
  }

  const displayName   = tenant?.branding?.displayName || tenant?.name;
  const primaryColor  = tenant?.branding?.primaryColor;
  const logoUrl       = tenant?.branding?.logoUrl;
  const bannerText    = tenant?.branding?.bannerText;

  const brandStyle = primaryColor ? { '--tenant-color': primaryColor } : {};
  const base = `/t/${tenantSlug}`;
  const loginUrl = `${base}/login?${new URLSearchParams({ next: `${base}/join` })}`;
  const isMember = memberships.some((entry) => entry.status === 'active'
    && (entry.tenantSlug === tenantSlug || String(entry.tenantId) === String(tenant?._id)));
  const hasApplication = !!claim;

  return (
    <div className="join-page" style={brandStyle}>
      <div className="join-card">
      {/* ── Tenant branding header ── */}
      <div className="join-header" style={primaryColor ? { borderTopColor: primaryColor } : {}}>
        {logoUrl && (
          <img src={logoUrl} alt={`${displayName} logo`} className="join-header__logo" />
        )}
        <h1 className="join-header__name">{displayName}</h1>
        {bannerText && <p className="join-header__banner">{bannerText}</p>}
        {tenant?.description && <p className="join-header__desc">{tenant.description}</p>}
      </div>
      <RSMinSignInButton tenantSlug={tenantSlug} />

      {/* ── Success message ── */}
      {success && (
        <div className="join-success" role="status">
          <p>{success}</p>
          {!user && <Link to={loginUrl} className="btn">Sign in to check your registration</Link>}
          {user && <button onClick={() => { setSuccess(''); setRevision((value) => value + 1); }}>Check approval status</button>}
        </div>
      )}

      {/* ── Content (hidden after success) ── */}
      {!success && (
        <div className="join-body">
          {user && (claimLoading || claimError || isMember || hasApplication) ? (
            <div className="join-status">
              <h2>Your registration</h2>
              {claimLoading ? <p role="status">Checking your registration…</p>
                : claimError ? <div role="alert"><p>{claimError}</p>
                  <button onClick={() => setRevision((value) => value + 1)}>Retry registration status</button></div>
                  : isMember ? <><p>Your access has been approved.</p><Link to={`${base}/dashboard`}>Open {displayName}</Link></>
                    : claim.resolvedStatus === 'community_verified' ? <><p>Your application has been approved.</p>
                      <button onClick={async () => {
                        setClaimError('');
                        try { await loadSession(true); } catch { setClaimError('Unable to refresh your access. Please retry.'); }
                      }}>Refresh community access</button></>
                      : ['revoked', 'unverified'].includes(claim.resolvedStatus) ? <p>Your application is not approved. Contact the community administrator.</p>
                        : <p role="status">Your registration is awaiting approval. A {displayName} administrator will review it and assign your access.</p>}
              {!claimLoading && !claimError && !isMember && claim?.resolvedStatus !== 'community_verified'
                && <button onClick={() => setRevision((value) => value + 1)}>Check approval status</button>}
            </div>
          ) : user ? (
            // ── Authenticated: claim form ──────────────────────────────────
            <div className="join-claim">
              <h2>Register with {displayName}</h2>
              <p>
                You are signed in as <strong>{user.email}</strong>. Submit a membership request
                to join <strong>{displayName}</strong>. A community administrator will review your
                request.
              </p>
              <form onSubmit={handleClaim}>
                <label>
                  Note to administrator (optional)
                  <textarea
                    value={claimNote}
                    onChange={(e) => setClaimNote(e.target.value)}
                    rows={3}
                    placeholder="Briefly describe why you're requesting membership or your connection to this community."
                  />
                </label>
                {msg && <p className="error-msg" role="alert">{msg}</p>}
                <div className="form-actions">
                  <button type="submit" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Request Membership'}
                  </button>
                  <Link to={`${base}/login`} className="btn-secondary">Back to sign in</Link>
                </div>
              </form>
            </div>
          ) : (
            // ── Unauthenticated: register + auto-claim ─────────────────────
            <div className="join-register">
              <h2>Register with {displayName}</h2>
              <p>
                Create your account for <strong>{displayName}</strong>. A community administrator
                will approve your registration and assign access to the appropriate tools.
              </p>
              <p className="join-login-hint">
                Already have an account? <Link to={loginUrl}>Sign in</Link>
              </p>
              <form onSubmit={handleRegisterAndJoin}>
                <div className="form-row">
                  <label>
                    First Name <span aria-hidden="true">*</span>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Last Name <span aria-hidden="true">*</span>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </label>
                </div>
                <label>
                  Email <span aria-hidden="true">*</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </label>
                <label>
                  Password <span aria-hidden="true">*</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  <small>Minimum 8 characters.</small>
                </label>
                <label>
                  Confirm Password <span aria-hidden="true">*</span>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </label>
                {msg && <p className="error-msg" role="alert">{msg}</p>}
                <div className="form-actions">
                  <button type="submit" disabled={submitting}>
                    {submitting ? 'Creating account…' : 'Register'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default TenantJoinPage;
